import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import WorkoutBreakdownTable from "./WorkoutTable";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { downloadExercisePlan, fetchExercise } from "api/exercisePlan";
import ActualDietPlanLoader from "@components/ui/Loaders/ActualDietPlanLoader";
import ExerciseDescCardView from "@features/exercise/ExerciseDescCardView";
import ButtonLoader from "@components/ui/Loaders/ButtonLoader";
import GridCards from "@components/ui/GridCards";
import { getData } from "data/uiData/CardsData/getExerciseViewPlanData";

const ViewPlan = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { day = null } = location.state || {};

  // State management
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch exercise data on component mount
  useEffect(() => {
    const loadExerciseData = async () => {
      // Check if day parameter exists
      if (!day) {
        navigate(-1);
        return;
      }

      try {
        setIsLoading(true);

        // Fetch data from API
        const response = await fetchExercise(day);
        setData(response?.plan?.data);
        setVideoUrl(response?.plan?.video_url);
      } catch (error) {
        console.error("Error fetching exercise data:", error);
        toast.error(t("Failed to load exercise data. Please try again"));
      } finally {
        setIsLoading(false);
      }
    };

    loadExerciseData();
  }, [day, navigate, t]);

  // Nutrient data configuration
  const exerciseCardsData = getData(data, t);

  const handleDownloadPdf = async () => {
    try {
      setLoading(true);
      const payload = {
        day: day,
      };
      const response = await downloadExercisePlan(payload);

      const fileBlob = new Blob([response.data], { type: "application/pdf" });
      const downloadUrl = window.URL.createObjectURL(fileBlob);

      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `${data?.title}.pdf`;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
      toast.info(t("toast.Exercise PDF downloaded successfully"));
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error(t("toast.Failed to download the file. Please try again"));
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (isLoading) {
    return <ActualDietPlanLoader exercisePlan={true} />;
  }

  return (
    <>
      <div
        className="mt-[30px] lg:mt-[60px] avoid-page-break p-6 rounded-[18px]"
        style={{
          background: "linear-gradient(150deg, #0771EF 12.89%, #15AAFC 85.22%)",
        }}
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-8">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              className="go-back-button p-[6px] lg:p-[10px] bg-white rounded-[100px] text-nowrap"
            >
              <ChevronLeft
                className="w-4 h-4 md:w-6 md:h-6 text-[#0575e6] rounded-full p-[2px]"
                strokeWidth={4}
              />
            </button>
            <h2 className="text-[16px] lg:text-[20px] text-white font-[500] font-[Lato]">
              {data?.title}
            </h2>
          </div>
          <button
            onClick={handleDownloadPdf}
            disabled={loading}
            className="py-[10px] px-3 md:px-[20px] bg-white text-[#0575e6] rounded-[100px] text-[12px]"
          >
            {loading ? <ButtonLoader /> : t("exercisePlan.downloadPlan")}
          </button>
        </div>

        <div className="bg-white rounded-[10px] p-5 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] p-5 rounded-[16px]">
              <h1 className="text-[18px] text-white font-[500] font-[Lato] mb-3">
                {t("exercisePlan.workoutSummary")}
              </h1>

              <GridCards
                data={exerciseCardsData}
                className="grid grid-cols-2 xl:grid-cols-3 gap-3"
              />
            </div>
            <div className="min-h-[200px] md:min-h-[300px] h-full">
              <video
                className="w-full h-full rounded-md shadow-lg object-cover"
                src={videoUrl}
                poster={data?.image_url}
                playsInline
                controls
              ></video>
            </div>
          </div>
          <div
            className="mt-[30px] lg:mt-[60px] avoid-page-break  p-6 rounded-[18px]"
            style={{
              background:
                "linear-gradient(150deg, #0771EF 12.89%, #15AAFC 85.22%)",
            }}
          >
            <div>
              <h1 className="text-[16px] lg:text-[20px] text-white font-[500] font-[Lato]">
                {t("exercisePlan.workoutDescription")}
              </h1>
              {/* <p className="text-[#6B6B6B] text-[17px] font-[400] font-[Lato]">
              {data?.description}
            </p> */}
              {data?.description && (
                <ExerciseDescCardView exercise={data?.description} />
              )}
            </div>
          </div>

          <WorkoutBreakdownTable breakdown={data?.instances} />
        </div>
      </div>
    </>
  );
};

export default ViewPlan;
